import { inject, observer } from "mobx-react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ClientManagementStore from "../../../modules/Admin/ClientManagement/store";
import {
  ClientCategoryDto,
  ClientDto,
} from "../../../modules/Admin/ClientManagement/types";
import { MessageResultState } from "../../../types/general-types";
import { DEFAULT_CATEGORY_ID, getCategoryName } from "../../config/categories";
import { REDESIGN_NS } from "../../i18n";
import useAutoTranslate from "../../lib/useAutoTranslate";
import { IconSliders } from "../../ui/icons";
import { Modal, ModalFooter } from "../../ui/Modal";
import CategoryDrawer from "./CategoryDrawer";
import Select from "../../ui/Select";

const EMPTY: ClientDto = {
  id: "",
  name: "",
  name_EN: "",
  description: "",
  description_EN: "",
  redirectUri: "",
  logoutUri: "",
  logoUrl: "",
  isLocal: false,
  permissionPrefix: "",
  orderIndex: 0,
  clientCategoryId: DEFAULT_CATEGORY_ID,
};

interface Props {
  ClientManagementStore?: ClientManagementStore;
  isOpen: boolean;
  onClose: () => void;
  /** Yoksa yeni kayıt. */
  selected?: ClientDto;
  categories: ClientCategoryDto[];
  /** Yönetim panelinde bir kategori değiştiğinde listeyi tazeletir. */
  onCategoriesChanged: (portalsMoved: boolean) => void;
  onSaved: () => void;
}

const PortalFormModal: FC<Props> = inject("ClientManagementStore")(
  observer(
    ({
      ClientManagementStore: store,
      isOpen,
      onClose,
      selected,
      categories,
      onCategoriesChanged,
      onSaved,
    }: Props) => {
      const { t, i18n } = useTranslation(REDESIGN_NS);
      const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);

      /* Pasif kategoriler seçilemez, ama DÜZENLENEN portal zaten pasif bir
         kategorideyse o kategori listede kalır: aksi halde Select boş bir
         değere düşer ve kaydetme sessizce kategoriyi değiştirirdi. */
      const selectableCategories = categories.filter(
        (category) =>
          category.isActive || category.id === selected?.clientCategoryId
      );
      const { register, handleSubmit, errors } = useForm();

      const [data, setData] = useState<ClientDto>(selected || EMPTY);
      const [isBusy, setIsBusy] = useState(false);
      const scheduleTranslate = useAutoTranslate(store);

      useEffect(() => {
        setData(selected || EMPTY);
      }, [selected]);

      const set = (patch: Partial<ClientDto>) =>
        setData((current) => ({ ...current, ...patch }));

      const submit = handleSubmit(async () => {
        if (!store) return;
        setIsBusy(true);
        try {
          const response = data.id
            ? await store.update(data)
            : await store.create(data);
          if (response.state === MessageResultState.SUCCESS) onSaved();
        } finally {
          setIsBusy(false);
        }
      });

      const required = t("Please fill out this field.").toString();

      const invalid = (name: string) => (errors[name] ? " is-invalid" : "");

      const error = (name: string) =>
        errors[name] ? (
          <div className="sso-rd__error">{errors[name].message}</div>
        ) : null;

      return (
        <>
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            width={900}
            title={data.id ? t("Update Portal") : t("Add New Portal")}
            footer={
              <ModalFooter
                onClose={onClose}
                onSubmit={submit}
                submitLabel={t("Save")}
                isBusy={isBusy}
              />
            }
          >
            <div className="sso-rd__form-grid">
              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-name">
                  {t("Name")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-name"
                  name="name"
                  type="text"
                  placeholder={t("Name")}
                  className={"sso-rd__input" + invalid("name")}
                  value={data.name}
                  ref={register({ required })}
                  onChange={(event) => {
                    set({ name: event.target.value });
                    scheduleTranslate(event.target.value, (text) =>
                      set({ name_EN: text })
                    );
                  }}
                />
                {error("name")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-name-en">
                  {t("Name (EN)")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-name-en"
                  name="name_EN"
                  type="text"
                  placeholder={t("Name (EN)")}
                  className={"sso-rd__input" + invalid("name_EN")}
                  value={data.name_EN}
                  ref={register({ required })}
                  onChange={(event) => set({ name_EN: event.target.value })}
                />
                {error("name_EN")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-desc">
                  {t("Description")}
                  <span className="sso-rd__req">*</span>
                </label>
                <textarea
                  id="sso-rd-desc"
                  name="description"
                  rows={3}
                  placeholder={t("Description")}
                  className={"sso-rd__textarea" + invalid("description")}
                  value={data.description}
                  ref={register({ required })}
                  onChange={(event) => {
                    set({ description: event.target.value });
                    scheduleTranslate(event.target.value, (text) =>
                      set({ description_EN: text })
                    );
                  }}
                />
                {error("description")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-desc-en">
                  {t("Description (EN)")}
                  <span className="sso-rd__req">*</span>
                </label>
                <textarea
                  id="sso-rd-desc-en"
                  name="description_EN"
                  rows={3}
                  placeholder={t("Description (EN)")}
                  className={"sso-rd__textarea" + invalid("description_EN")}
                  value={data.description_EN}
                  ref={register({ required })}
                  onChange={(event) => set({ description_EN: event.target.value })}
                />
                {error("description_EN")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-redirect">
                  {t("Redirect Uri")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-redirect"
                  name="redirectUri"
                  type="text"
                  placeholder="https://"
                  className={"sso-rd__input" + invalid("redirectUri")}
                  value={data.redirectUri}
                  ref={register({ required })}
                  onChange={(event) => set({ redirectUri: event.target.value })}
                />
                {error("redirectUri")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-logout">
                  {t("Logout Uri")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-logout"
                  name="logoutUri"
                  type="text"
                  placeholder="https://"
                  className={"sso-rd__input" + invalid("logoutUri")}
                  value={data.logoutUri}
                  ref={register({ required })}
                  onChange={(event) => set({ logoutUri: event.target.value })}
                />
                {error("logoutUri")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-prefix">
                  {t("Permission Prefix")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-prefix"
                  name="permissionPrefix"
                  type="text"
                  placeholder={t("Prefix")}
                  className={"sso-rd__input" + invalid("permissionPrefix")}
                  value={data.permissionPrefix}
                  ref={register({ required })}
                  onChange={(event) => set({ permissionPrefix: event.target.value })}
                />
                {error("permissionPrefix")}
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-category">
                  {t("Category")}
                  <span className="sso-rd__req">*</span>
                </label>
                {/* Yönetim butonu dropdown'ın yanında: kategori eklemek için
                    portal formundan çıkmak gerekmiyor. */}
                <div className="sso-rd__field-inline">
                  <Select
                    id="sso-rd-category"
                    value={String(data.clientCategoryId ?? DEFAULT_CATEGORY_ID)}
                    onChange={(next) => set({ clientCategoryId: Number(next) })}
                    options={selectableCategories.map((def) => ({
                      value: String(def.id),
                      label: getCategoryName(def, i18n.language),
                    }))}
                  />
                  <button
                    type="button"
                    className="sso-rd__icon-btn sso-rd__icon-btn--neutral"
                    onClick={() => setIsCategoryDrawerOpen(true)}
                    title={t("Manage Categories")}
                    aria-label={t("Manage Categories")}
                  >
                    <IconSliders size={17} />
                  </button>
                </div>
              </div>

              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-order">
                  {t("Appearance Order")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-order"
                  name="orderIndex"
                  type="number"
                  placeholder="0"
                  className={"sso-rd__input" + invalid("orderIndex")}
                  value={data.orderIndex}
                  ref={register({ required })}
                  onChange={(event) =>
                    set({ orderIndex: Number(event.target.value) })
                  }
                />
                {error("orderIndex")}
              </div>

              <div className="sso-rd__field">
                <span className="sso-rd__label">{t("Is Local")}</span>
                <label className="sso-rd__check sso-rd__check--lg">
                  <input
                    type="checkbox"
                    checked={data.isLocal}
                    onChange={(event) => set({ isLocal: event.target.checked })}
                    aria-label={t("Is Local")}
                  />
                </label>
              </div>
            </div>
          </Modal>

        <CategoryDrawer
          isOpen={isCategoryDrawerOpen}
          onClose={() => setIsCategoryDrawerOpen(false)}
          categories={categories}
          onChanged={onCategoriesChanged}
        />
        </>
      );
    }
  )
) as FC<Props>;

export default PortalFormModal;
